import { ChevronDown } from "lucide-react";
import React from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

export interface IWhatsappMetaTemplateExpandableProps {
  template: Record<string, unknown>;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function MetaComponentBlock({ comp }: { comp: Record<string, unknown> }): React.ReactNode {
  const { t } = useI18n();
  const type = typeof comp.type === "string" ? comp.type : "UNKNOWN";

  if (type === "BUTTONS") {
    if (!Array.isArray(comp.buttons)) {
      return (
        <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2 text-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("campaigns.whatsappMeta.templatesDetail.component.buttons")}
          </div>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-xs text-foreground/90">
            {formatValue(comp)}
          </pre>
        </div>
      );
    }
    return (
      <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2 text-sm">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("campaigns.whatsappMeta.templatesDetail.component.buttons")}
        </div>
        <ul className="mt-2 list-inside list-disc space-y-1.5 text-foreground/90">
          {(comp.buttons as Record<string, unknown>[]).map((btn, bi) => (
            <li key={bi}>
              <span className="font-mono text-xs text-muted-foreground">
                {formatValue(btn.type)}
              </span>
              {btn.text != null ? (
                <span className="ml-1">{formatValue(btn.text)}</span>
              ) : null}
              {btn.url != null ? (
                <div className="ml-4 font-mono text-xs break-all text-primary">
                  {formatValue(btn.url)}
                </div>
              ) : null}
              {btn.phone_number != null ? (
                <div className="ml-4 font-mono text-xs">
                  {formatValue(btn.phone_number)}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const text =
    comp.text != null && typeof comp.text === "string"
      ? comp.text
      : comp.text != null
        ? formatValue(comp.text)
        : null;

  const format =
    comp.format != null ? (
      <span className="ml-2 font-mono text-xs text-muted-foreground">
        ({formatValue(comp.format)})
      </span>
    ) : null;

  const example =
    comp.example != null ? (
      <div className="mt-2 rounded border border-dashed border-border/80 bg-background/80 px-2 py-1.5 font-mono text-xs text-muted-foreground whitespace-pre-wrap">
        {formatValue(comp.example)}
      </div>
    ) : null;

  return (
    <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {type}
        </span>
        {format}
      </div>
      {text ? (
        <p className="mt-2 whitespace-pre-wrap text-foreground/95">{text}</p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("campaigns.whatsappMeta.templatesDetail.component.noText")}
        </p>
      )}
      {example}
    </div>
  );
}

function TemplateStructure({ components }: { components: unknown }): React.ReactNode {
  const { t } = useI18n();

  if (components == null) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("campaigns.whatsappMeta.templatesDetail.noComponents")}
      </p>
    );
  }

  if (!Array.isArray(components)) {
    return (
      <pre className="max-h-64 overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs">
        {formatValue(components)}
      </pre>
    );
  }

  if (components.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("campaigns.whatsappMeta.templatesDetail.emptyComponents")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {components.map((raw, idx) => {
        const comp =
          raw != null && typeof raw === "object"
            ? (raw as Record<string, unknown>)
            : null;
        if (!comp) {
          return null;
        }
        return <MetaComponentBlock key={idx} comp={comp} />;
      })}
    </div>
  );
}

export const WhatsappMetaTemplateExpandable: React.FC<
  IWhatsappMetaTemplateExpandableProps
> = ({ template }) => {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);

  const name = formatValue(template.name);
  const status = formatValue(template.status);
  const language = formatValue(template.language);
  const category = template.category != null ? formatValue(template.category) : null;
  const templateId = template.id != null ? formatValue(template.id) : null;
  const quality =
    template.quality_score != null ? formatValue(template.quality_score) : null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border/80 bg-card/30">
        <CollapsibleTrigger className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40">
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open ? "rotate-180" : "rotate-0"
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-foreground">{name}</div>
            <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span>{status}</span>
              <span aria-hidden>·</span>
              <span>{language}</span>
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 border-t border-border/60 px-3 py-4 text-sm">
            <div className="grid gap-2 text-xs sm:grid-cols-2">
              {category ? (
                <div>
                  <span className="text-muted-foreground">
                    {t("campaigns.whatsappMeta.templatesDetail.category")}
                    {": "}
                  </span>
                  <span className="font-mono">{category}</span>
                </div>
              ) : null}
              {templateId ? (
                <div>
                  <span className="text-muted-foreground">
                    {t("campaigns.whatsappMeta.templatesDetail.templateId")}
                    {": "}
                  </span>
                  <span className="font-mono break-all">{templateId}</span>
                </div>
              ) : null}
              {quality ? (
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">
                    {t("campaigns.whatsappMeta.templatesDetail.qualityScore")}
                    {": "}
                  </span>
                  <span className="font-mono break-all">{quality}</span>
                </div>
              ) : null}
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("campaigns.whatsappMeta.templatesDetail.structure")}
              </div>
              <TemplateStructure components={template.components} />
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
